import type { PDFDocumentProxy } from 'pdfjs-dist';

import { loadPdfJs } from './pdfJsLoader';
import { STUDY_ANALYSIS_PAGE_MAX_BYTES, type StudyRegion } from './studyAnalysisApi';
import {
  fitWithin,
  laplacianVariance,
  looksBlurry,
  looksDark,
  pdfRenderScale,
  regionToPixels,
  rotatedSize,
  sharpnessSampleSize,
  shrinkForBytes,
  toGrayscale,
  type PageRotation,
  type Size,
} from './studyPageGeometry';

/**
 * Browser work for Study page images (study-capture-analyse-prd.md §3.2). Photos are decoded and
 * re-encoded here, so the server receives a PNG without photo metadata, at most 1600 px per side
 * and 2 MB. PDF pages use the existing lazy pdf.js loader. Nothing here calls AI.
 */

export const HEIC_UNREADABLE_MESSAGE =
  "This photo can't be read here. Use Take a photo or upload a JPG.";
const UNREADABLE_MESSAGE = "This photo can't be read. Choose another photo.";
const TOO_DETAILED_MESSAGE = 'This page has too much detail to send. Take a closer photo.';
const CANVAS_MESSAGE = 'Page preparation is not available in this browser. Try another browser.';
/** Decoding limit, the same as the Tutor image check. */
const MAX_PIXELS = 24_000_000;
const DARK_SAMPLE_EDGE = 48;

export interface EncodedPage {
  blob: Blob;
  width: number;
  height: number;
}

export interface PreparedPhoto extends EncodedPage {
  /** The decoded photo before resizing; used for the quality hints. */
  original: Size;
  blurry: boolean;
  dark: boolean;
}

interface DecodedImage {
  source: CanvasImageSource;
  size: Size;
  release: () => void;
}

function isHeic(blob: Blob, name = ''): boolean {
  return /image\/hei[cf]/i.test(blob.type) || /\.hei[cf]$/i.test(name);
}

/**
 * Decodes a photo. `createImageBitmap` decodes the FIRST frame of an animated GIF (HTML spec), so
 * a GIF becomes one still page PNG like any other photo; the `<img>` fallback draws the first
 * frame too, because it is drawn as soon as it loads.
 */
async function decodeImage(blob: Blob, name?: string): Promise<DecodedImage> {
  const failure = () =>
    new Error(isHeic(blob, name) ? HEIC_UNREADABLE_MESSAGE : UNREADABLE_MESSAGE);
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
      return {
        source: bitmap,
        size: { width: bitmap.width, height: bitmap.height },
        release: () => bitmap.close(),
      };
    } catch {
      // Some browsers decode a format only through <img>. Try that before giving up.
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(failure());
      image.src = url;
    });
    if (!image.naturalWidth || !image.naturalHeight) throw failure();
    return {
      source: image,
      size: { width: image.naturalWidth, height: image.naturalHeight },
      release: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error instanceof Error ? error : failure();
  }
}

function canvas2d(size: Size): { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error(CANVAS_MESSAGE);
  return { canvas, context };
}

export function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error(CANVAS_MESSAGE));
    }, 'image/png');
  });
}

/** Draws `source` at `target` size, turned by `rotation`, on a white page. */
function drawRotated(source: CanvasImageSource, target: Size, rotation: PageRotation) {
  const { canvas, context } = canvas2d(rotatedSize(target, rotation));
  context.fillStyle = 'white';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate((rotation * Math.PI) / 180);
  context.drawImage(source, -target.width / 2, -target.height / 2, target.width, target.height);
  return canvas;
}

/** Encodes at most 1600 px per side, shrinking until the PNG is within 2 MB. */
async function encodeWithinLimits(
  draw: (target: Size) => HTMLCanvasElement | Promise<HTMLCanvasElement>,
  natural: Size,
): Promise<EncodedPage> {
  let target: Size | null = fitWithin(natural);
  while (target) {
    const canvas = await draw(target);
    const blob = await canvasToPng(canvas);
    if (blob.size <= STUDY_ANALYSIS_PAGE_MAX_BYTES) {
      return { blob, width: canvas.width, height: canvas.height };
    }
    target = shrinkForBytes(target, blob.size, STUDY_ANALYSIS_PAGE_MAX_BYTES);
  }
  throw new Error(TOO_DETAILED_MESSAGE);
}

function sampleDark(source: CanvasImageSource): boolean {
  try {
    const { context } = canvas2d({ width: DARK_SAMPLE_EDGE, height: DARK_SAMPLE_EDGE });
    context.drawImage(source, 0, 0, DARK_SAMPLE_EDGE, DARK_SAMPLE_EDGE);
    return looksDark(context.getImageData(0, 0, DARK_SAMPLE_EDGE, DARK_SAMPLE_EDGE).data);
  } catch {
    return false;
  }
}

/**
 * Sharpness of a photo: the Laplacian variance of a downscaled grayscale copy
 * (study-capture-fixes-prd.md F-12). Null when the browser cannot read the pixels.
 */
function sampleSharpness(source: CanvasImageSource, size: Size): number | null {
  try {
    const sample = sharpnessSampleSize(size);
    const { context } = canvas2d(sample);
    context.drawImage(source, 0, 0, sample.width, sample.height);
    const pixels = context.getImageData(0, 0, sample.width, sample.height).data;
    return laplacianVariance(toGrayscale(pixels), sample.width, sample.height);
  } catch {
    return null;
  }
}

/** Decodes, rotates and resizes one photo into an analysis page PNG. */
export async function preparePhotoPage(
  file: Blob,
  rotation: PageRotation = 0,
  name?: string,
): Promise<PreparedPhoto> {
  const decoded = await decodeImage(file, name);
  try {
    if (decoded.size.width * decoded.size.height > MAX_PIXELS) {
      throw new Error('This photo is too large. Choose a photo under 24 megapixels.');
    }
    const encoded = await encodeWithinLimits(
      (target) => drawRotated(decoded.source, target, rotation),
      decoded.size,
    );
    return {
      ...encoded,
      original: decoded.size,
      blurry: looksBlurry(decoded.size, sampleSharpness(decoded.source, decoded.size)),
      dark: sampleDark(decoded.source),
    };
  } finally {
    decoded.release();
  }
}

/** Opens a PDF from a local file or a signed read URL with the lazy pdf.js loader. */
export async function openPdf(source: Blob | string): Promise<PDFDocumentProxy> {
  const { getDocument } = await loadPdfJs();
  const task =
    typeof source === 'string'
      ? getDocument({ url: source })
      : getDocument({ data: new Uint8Array(await source.arrayBuffer()) });
  return task.promise;
}

/** Renders one PDF page as an analysis page PNG. */
export async function renderPdfPage(
  pdf: PDFDocumentProxy,
  pageNumber: number,
): Promise<EncodedPage> {
  const page = await pdf.getPage(pageNumber);
  const base = page.getViewport({ scale: 1 });
  const scale = pdfRenderScale({ width: base.width, height: base.height });
  const natural = { width: base.width * scale, height: base.height * scale };
  return encodeWithinLimits(async (target) => {
    const viewport = page.getViewport({ scale: target.width / base.width });
    const { canvas, context } = canvas2d({
      width: Math.max(1, Math.floor(viewport.width)),
      height: Math.max(1, Math.floor(viewport.height)),
    });
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvas, canvasContext: context, viewport }).promise;
    return canvas;
  }, natural);
}

/** Cuts a question region out of a page PNG for the Study source-region upload. */
export async function cropPageRegion(page: Blob, region: StudyRegion): Promise<Blob> {
  const decoded = await decodeImage(page);
  try {
    const rect = regionToPixels(region, decoded.size);
    if (!rect) throw new Error('This question area is outside the page.');
    const { canvas, context } = canvas2d({ width: rect.sw, height: rect.sh });
    context.drawImage(decoded.source, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, rect.sw, rect.sh);
    return await canvasToPng(canvas);
  } finally {
    decoded.release();
  }
}

/** Draws a page PNG onto a visible canvas (for the region overlay and highlights). */
export async function drawPageOnCanvas(page: Blob, canvas: HTMLCanvasElement): Promise<Size> {
  const decoded = await decodeImage(page);
  try {
    canvas.width = decoded.size.width;
    canvas.height = decoded.size.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error(CANVAS_MESSAGE);
    context.drawImage(decoded.source, 0, 0);
    return decoded.size;
  } finally {
    decoded.release();
  }
}
